import { getIssueDay } from '@weft/services';
import type { Contract, Invoice, Period } from '@weft/types';
import { dayjs } from '@weft/utils';
import { formatNumber } from '@weft/utils';
import { formatDate } from 'apps/app/src/utils/date';
import React from 'react';

type InvoiceHeaderProps = {
  invoice: Omit<Invoice, 'contract'>;
  contract: Contract;
  totalPrice: number;
  commitment: number;
  currencySymbol: string;
};

export const InvoiceHeader:React.FC<InvoiceHeaderProps> = ({
  invoice,
  contract,
  totalPrice,
  commitment,
  currencySymbol,
}) => {
  const issueDate = getIssueDay(contract.plan.billingData.paymentTerm, invoice.billingCycle);

  return (
    <>
      <div className='border-line mb-4 flex flex-wrap justify-between gap-6 border-b pb-4'>
        <div>
          <div className='text-3xl font-medium'>
            Pro forma Invoice
            {' '}
            <span className='text-inactive font-normal uppercase'>{getFormattedId(invoice.id)}</span>
          </div>

          <div className='flex items-center gap-1 text-sm'>
            <div className='text-inactive'>Billed by</div>
            <div className='text-black'>Company name</div>
          </div>
        </div>
        <div className='flex justify-between gap-20'>
          <div>
            <div className='text-inactive text-sm'>Amount Due</div>
            <div className='text-3xl text-black'>
              <span className='text-xl'>{currencySymbol}</span>
              {formatNumber(totalPrice)}
            </div>
          </div>
        </div>
      </div>
      <div>
        <div className='text-label text-sm'>Bill To</div>
        <div className='flex justify-between'>
          <div>
            <div className='text-xl font-medium text-black'>{contract.customer.name}</div>
            <div className='text-label max-w-[15rem] text-sm'>Main office Address # 0052154, City Name, Us</div>
          </div>
          <div>
            <div className='text-label text-sm'>Billing period</div>
            <div className='text-lg text-black'>{getFormattedBillingPeriod(invoice.billingCycle)}</div>
          </div>
          <div>
            <div className='text-label text-sm'>PO #</div>
            <div className='text-lg text-black'>-—</div>
          </div>
          {commitment ? (
            <div>
              <div className='text-label text-sm'>Commitment</div>
              <div className='text-lg text-black'>{commitment}</div>
            </div>
          ) : <></>}
          <div>
            <div className='text-label text-sm'>Issued</div>
            <div className='text-lg text-black'>{formatDate(issueDate)}</div>
          </div>
          <div>
            <div className='text-label text-sm'>Due Date</div>
            <div className="text-lg text-black">30 days</div>
          </div>
        </div>
      </div>
    </>
  );
};

const getFormattedId = (id: string, charCount = 5) => {
  const digits = -Math.abs(charCount);

  return id.slice(digits);
};

const getFormattedBillingPeriod = (billingCycle: Period): string => {
  const formattedStartTime = dayjs(billingCycle.startTime).utc().format('MMM. DD');
  const formattedEndTime = dayjs(billingCycle.endTime).utc().format('MMM. DD');

  return `${formattedStartTime} - ${formattedEndTime}`;
};